import { t } from 'i18next';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { StatusBadgeVariant } from '@/components/status-badge/status-badge.types';
import {
  CampaignEntryCounts,
  CampaignImportReport,
  CampaignImportSkippedRow,
} from '@/services/outbound-calls-service/campaigns.service.types';
import { SalesAgent } from '@/services/outbound-calls-service/salespeople.service.types';
import {
  CONCLUSION_CODES,
  CampaignStatus,
  ConclusionCode,
  DISPOSITIONS,
  Disposition,
  EntryStatus,
  IMPORT_ISSUES,
  SalesAgentStatus,
  TRANSFER_RESULTS,
  TRIGGER_SOURCES,
  TransferResult,
  TriggerSource,
} from '@/services/outbound-calls-service/outbound-calls.constants';
import { apiFieldErrors } from '@/utils/api-field-errors';
import { formatConversationDateTime } from '@/utils/helpers';
import { fieldErrorLabel, skipReasonLabel } from './outbound-calls.messages';

dayjs.extend(customParseFormat);

export const campaignPath = (id: number) => `/outbound-calls/campaigns/${id}`;

export const CAMPAIGN_STATUS_BADGE: Record<CampaignStatus, StatusBadgeVariant> = {
  draft: 'idle',
  running: 'live',
  paused: 'hand',
  finished: 'ok',
};

export const ENTRY_STATUS_BADGE: Record<EntryStatus, StatusBadgeVariant> = {
  pending: 'idle',
  calling: 'live',
  reached: 'ok',
  not_reached: 'hand',
  failed: 'fail',
  skipped: 'idle',
};

export const SALES_AGENT_STATUS_BADGE: Record<SalesAgentStatus, StatusBadgeVariant> = {
  offline: 'idle',
  available: 'ok',
  busy: 'live',
};

export const TRANSFER_RESULT_BADGE: Record<TransferResult, StatusBadgeVariant> = {
  connected: 'ok',
  no_answer: 'hand',
  unavailable: 'hand',
  failed: 'fail',
};

export const countOf = (counts: CampaignEntryCounts | undefined, ...statuses: EntryStatus[]) =>
  statuses.reduce((sum, status) => sum + (counts?.[status] ?? 0), 0);

/** Entries the campaign is done with, whatever the outcome. */
export const settledEntries = (counts: CampaignEntryCounts | undefined) =>
  countOf(counts, 'reached', 'not_reached', 'failed', 'skipped');

/** "09:00:00" and "09:00" both read as "09:00". */
export const toHourMinute = (value: string | null | undefined) => (value ?? '').slice(0, 5);

/** "HH:MM" into the value a TimePicker holds, and back. */
export const toTimeValue = (value: string | null | undefined) =>
  value ? dayjs(toHourMinute(value), 'HH:mm') : null;

export const fromTimeValue = (value: Dayjs | null | undefined) =>
  value ? value.format('HH:mm') : null;

/** [1, 2, 3, 4, 5, 7] -> "Pn–Pt, Nd". Consecutive days collapse into a range. */
export const formatCallDays = (days: number[] | undefined) => {
  const sorted = [...new Set(days ?? [])].sort((a, b) => a - b);
  const runs: number[][] = [];
  for (const day of sorted) {
    const run = runs[runs.length - 1];
    if (run && day === run[run.length - 1] + 1) run.push(day);
    else runs.push([day]);
  }
  const label = (day: number) => t(`outbound:weekdays.${day}` as 'outbound:weekdays.1');

  return runs
    .map((run) =>
      run.length > 2 ? `${label(run[0])}–${label(run[run.length - 1])}` : run.map(label).join(', ')
    )
    .join(', ');
};

/** A salesperson's schedule: no days means every day, no hours the whole day. */
export const workHoursLabel = ({
  work_days: workDays,
  work_start: start,
  work_end: end,
}: Pick<SalesAgent, 'work_days' | 'work_start' | 'work_end'>) => {
  const days = workDays.length ? formatCallDays(workDays) : t('outbound:salespeople.everyDay');
  if (start && end) {
    return t('outbound:salespeople.hoursValue', {
      days,
      start: toHourMinute(start),
      end: toHourMinute(end),
    });
  }
  return workDays.length
    ? t('outbound:salespeople.daysOnly', { days })
    : t('outbound:salespeople.allTheTime');
};

export const formatDateTime = (value: string | null | undefined) =>
  value ? formatConversationDateTime(value) : t('outbound:none');

const isDisposition = (value: string): value is Disposition =>
  (DISPOSITIONS as readonly string[]).includes(value);

/** The LLM may post a disposition outside the known list; that one shows as sent. */
export const dispositionLabel = (value: string | null | undefined) => {
  if (!value) return t('outbound:none');
  return isDisposition(value) ? t(`outbound:disposition.${value}`) : value;
};

export const triggerSourceLabel = (value: string | null | undefined) => {
  if (!value) return t('outbound:none');
  return (TRIGGER_SOURCES as readonly string[]).includes(value)
    ? t(`outbound:triggerSource.${value as TriggerSource}`)
    : value;
};

export const transferResultLabel = (value: string | null | undefined) => {
  if (!value) return t('outbound:call.facts.notTransferred');
  return (TRANSFER_RESULTS as readonly string[]).includes(value)
    ? t(`outbound:transferResult.${value as TransferResult}`)
    : value;
};

export const objectiveLabel = (value: boolean | null | undefined) => {
  if (value === true) return t('outbound:objective.completed');
  if (value === false) return t('outbound:objective.notCompleted');
  return null;
};

/** Polish when the scenario speaks it, whatever order the API lists its languages in. */
export const preferredLanguage = (languages: string[]) =>
  languages.find((code) => code.toLowerCase().startsWith('pl')) ?? languages[0];

/** How many rows of the import report each issue covers, in the report's order. */
export const issueCounts = (rows: CampaignImportSkippedRow[]) =>
  IMPORT_ISSUES.map((code) => ({
    code,
    count: rows.filter((row) => row.code === code).length,
  })).filter(({ count }) => count > 0);

const csvCell = (value: string) =>
  /[;"\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

/**
 * The skipped and warned rows of an import report, in file order, as a CSV Excel opens as is:
 * a BOM for the Polish letters and a semicolon, the delimiter of Polish Excel.
 */
export const importReportCsv = ({ skipped, warnings }: CampaignImportReport) => {
  const rows = [
    ...skipped.map((row) => ({ row, outcome: t('outbound:import.rowSkipped') })),
    ...warnings.map((row) => ({ row, outcome: t('outbound:import.rowWarning') })),
  ].sort((a, b) => a.row.line - b.row.line);
  const lines = [
    [t('outbound:import.line'), t('outbound:import.outcome'), t('outbound:import.reason')],
    ...rows.map(({ row, outcome }) => [String(row.line), outcome, skipReasonLabel(row.reason)]),
  ];
  return `\uFEFF${lines.map((cells) => cells.map(csvCell).join(';')).join('\r\n')}\r\n`;
};

const CONCLUSION = /^([a-z_]+)(?:\s*:\s*([\s\S]*))?$/i;

/**
 * `conclusion` opens with a reason code and a colon. A known code becomes a label and the rest
 * stays the agent's words; a conclusion without one is all words.
 */
export const conclusionParts = (conclusion: string | null | undefined) => {
  if (!conclusion) return null;
  const match = CONCLUSION.exec(conclusion.trim());
  const code = match?.[1].toLowerCase();
  if (match && code && (CONCLUSION_CODES as readonly string[]).includes(code)) {
    return {
      label: t(`outbound:conclusion.${code as ConclusionCode}`),
      detail: match[2]?.trim() ?? '',
    };
  }
  return { label: null, detail: conclusion };
};

const extractedValue = (value: unknown) => {
  if (value === null || value === undefined || value === '') return t('outbound:none');
  if (typeof value === 'boolean') return value ? t('common:states.yes') : t('common:states.no');
  return typeof value === 'string' ? value : JSON.stringify(value);
};

/**
 * `extracted_data` values are whatever the LLM collected: null for what the callee never
 * said, booleans for yes/no answers, and anything else non-string renders as JSON.
 */
export const extractedDataRows = (data: Record<string, unknown> | null | undefined) =>
  Object.entries(data ?? {}).map(([key, value]) => ({ key, value: extractedValue(value) }));

type FieldErrorOptions<Field extends string> = {
  /** An API key the form edits under another name. */
  aliases?: Partial<Record<string, Field>>;
  /** What "has already been taken" means on this form's fields. */
  taken?: Partial<Record<Field, string>>;
};

/** A rejected save's per-field messages in Polish, keyed by form field. */
export const formFieldErrors = <Field extends string>(
  error: unknown,
  fields: readonly Field[],
  { aliases = {}, taken = {} }: FieldErrorOptions<Field> = {}
) =>
  Object.entries(apiFieldErrors(error) ?? {}).flatMap(([key, messages]) => {
    const name = aliases[key] ?? key;
    if (!(fields as readonly string[]).includes(name)) return [];
    const field = name as Field;
    return [
      {
        name: field,
        errors: messages.map((message) =>
          message === 'has already been taken' && taken[field]
            ? taken[field]
            : fieldErrorLabel(message)
        ),
      },
    ];
  });
